import { useState, useEffect, useCallback, useMemo } from 'react';
import { NotificationRule, NotificationRuleWithUser } from '../../types/notifications';
import { notificationRulesApi } from '../../api/notificationRulesApi';
import { useUsersData } from '../../Helpers/UsersContext';

interface UseNotificationRulesReturn {
    rules: NotificationRuleWithUser[];
    loading: boolean;
    error: string | null;
    submitting: boolean;
    createRule: (userId: string, buildingTypes: string[], buildings: string[]) => Promise<void>;
    updateRule: (userId: string, buildingTypes: string[], buildings: string[]) => Promise<void>;
    deleteRule: (userId: string) => Promise<void>;
    refresh: () => Promise<void>;
    clearError: () => void;
}

export const useNotificationRules = (): UseNotificationRulesReturn => {
    const [rules, setRules] = useState<NotificationRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const { users } = useUsersData();

    // Helper to get user name by UID
    const getUserName = useCallback((uid: string): string => {
        const user = users?.find(u => u.key === uid);
        return user?.name || uid;
    }, [users]);

    // Enrich rules with user names
    const rulesWithUsers = useMemo((): NotificationRuleWithUser[] => {
        return rules.map(rule => ({
            ...rule,
            userName: getUserName(rule.userId),
        }));
    }, [rules, getUserName]);

    // Load rules
    const loadRules = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const loadedRules = await notificationRulesApi.getRules();
            setRules(loadedRules);
        } catch (err) {
            console.error('Error loading notification rules:', err);
            setError('Kunde inte ladda aviseringsregler');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load on mount
    useEffect(() => {
        loadRules();
    }, [loadRules]);

    // Create new rule
    const createRule = useCallback(async (
        userId: string,
        buildingTypes: string[],
        buildings: string[]
    ) => {
        setSubmitting(true);
        setError(null);

        try {
            await notificationRulesApi.createRule(userId, buildingTypes, buildings);
            await loadRules();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Kunde inte skapa regel';
            console.error('Error creating notification rule:', err);
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [loadRules]);

    // Update existing rule
    const updateRule = useCallback(async (
        userId: string,
        buildingTypes: string[],
        buildings: string[]
    ) => {
        setSubmitting(true);
        setError(null);

        try {
            await notificationRulesApi.updateRule(userId, buildingTypes, buildings);
            await loadRules();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Kunde inte uppdatera regel';
            console.error('Error updating notification rule:', err);
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [loadRules]);

    // Delete rule
    const deleteRule = useCallback(async (userId: string) => {
        setSubmitting(true);
        setError(null);

        try {
            await notificationRulesApi.deleteRule(userId);
            await loadRules();
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Kunde inte ta bort regel';
            console.error('Error deleting notification rule:', err);
            setError(message);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [loadRules]);

    // Clear error
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        rules: rulesWithUsers,
        loading,
        error,
        submitting,
        createRule,
        updateRule,
        deleteRule,
        refresh: loadRules,
        clearError,
    };
};
